/**
 * Zoom and Pan Utility Functions
 * Reusable utilities for image zoom and pan functionality
 * Used in: CoffeeCommunities, PersonalChat
 */

/**
 * Clamp a value between min and max
 * @param {number} v - value to clamp
 * @param {number} a - min value
 * @param {number} b - max value
 * @returns {number} clamped value
 */
export const clamp = (v, a, b) => Math.max(a, Math.min(b, v))

/**
 * Initialize zoom state refs
 * Returns an object containing all necessary refs for zoom/pan functionality
 * @returns {object} object containing all required refs
 */
export const initializeZoomRefs = () => ({
  zoomContainerRef: { current: null },
  imgRef: { current: null },
  scaleRef: { current: 1 },
  translateRef: { current: { x: 0, y: 0 } },
  isPanningRef: { current: false },
  lastPointerRef: { current: { x: 0, y: 0 } }
})

/**
 * Reset zoom to default state
 * @param {object} refs - object containing scaleRef, translateRef
 * @param {function} forceRerender - function to trigger re-render
 */
export const resetZoom = (refs, forceRerender) => {
  const { scaleRef, translateRef } = refs

  scaleRef.current = 1
  translateRef.current = { x: 0, y: 0 }
  forceRerender((n) => n + 1)
}

/**
 * Adjust translate bounds after zoom/pan to keep image within container
 * @param {object} refs - object containing zoomContainerRef, imgRef, scaleRef, translateRef
 * @param {function} forceRerender - function to trigger re-render
 */
export const adjustTranslateBounds = (refs, forceRerender) => {
  const { zoomContainerRef, imgRef, scaleRef, translateRef } = refs

  const container = zoomContainerRef.current
  const img = imgRef.current

  if (!container || !img) return

  const rect = container.getBoundingClientRect()
  const containerW = rect.width
  const containerH = rect.height

  const naturalW = img.naturalWidth || img.width || 0
  const naturalH = img.naturalHeight || img.height || 0

  if (!naturalW || !naturalH) return

  // fit image inside container (object-fit: contain)
  const fitScale = Math.min(containerW / naturalW, containerH / naturalH)
  const baseW = naturalW * fitScale
  const baseH = naturalH * fitScale

  const s = scaleRef.current || 1

  if (baseW * s <= containerW) {
    // no horizontal pan allowed
    translateRef.current.x = 0
  } else {
    const maxFinalShift = (baseW * s - containerW) / 2
    const maxTx = maxFinalShift / s

    translateRef.current.x = clamp(translateRef.current.x, -maxTx, maxTx)
  }

  if (baseH * s <= containerH) {
    translateRef.current.y = 0
  } else {
    const maxFinalShiftY = (baseH * s - containerH) / 2
    const maxTy = maxFinalShiftY / s

    translateRef.current.y = clamp(translateRef.current.y, -maxTy, maxTy)
  }

  forceRerender((n) => n + 1)
}

/**
 * Handle wheel zoom event - zooms to center of container
 * @param {object} refs - object containing all zoom/pan refs
 * @param {function} forceRerender - function to trigger re-render
 * @param {function} adjustBounds - function to adjust translate bounds after zoom
 * @returns {function} wheel event handler
 */
export const handleWheelZoom = (refs, forceRerender, adjustBounds) => (e) => {
  const { zoomContainerRef, scaleRef, translateRef } = refs

  if (!zoomContainerRef.current) return

  // Prevent page scroll when zooming
  e.preventDefault()

  // Use exponential zoom for smooth feel
  const zoomFactor = Math.exp(-e.deltaY * 0.0018)

  const prevScale = scaleRef.current
  const newScale = clamp(prevScale * zoomFactor, 1, 5)

  // If no scale change, bail
  if (Math.abs(newScale - prevScale) < 1e-6) return

  const rect = zoomContainerRef.current.getBoundingClientRect()

  // center of container - prefer center-based zoom (ignore cursor position)
  const centerX = rect.width / 2
  const centerY = rect.height / 2
  const cx = centerX
  const cy = centerY

  const tx = translateRef.current.x
  const ty = translateRef.current.y

  // Derived formula to keep the image point under cursor fixed:
  // t' = t + ((s1 - s0) / (s0 * s1)) * (cursor - center)
  const coef = (newScale - prevScale) / (prevScale * newScale)
  const nx = tx + coef * (cx - centerX)
  const ny = ty + coef * (cy - centerY)

  if (newScale <= 1.00001) {
    scaleRef.current = 1
    translateRef.current = { x: 0, y: 0 }
  } else {
    translateRef.current = { x: nx, y: ny }
    scaleRef.current = newScale
  }

  // trigger update
  forceRerender((n) => n + 1)

  // Clamp to bounds after zoom
  setTimeout(() => {
    try {
      adjustBounds()
    } catch (err) {
      console.log('Error adjusting translate bounds after zoom:', err)
    }
  }, 0)
}

/**
 * Handle pointer down to start panning
 * @param {object} refs - object containing isPanningRef, scaleRef, lastPointerRef
 * @returns {function} pointer down handler
 */
export const handlePointerDown = (refs) => (e) => {
  const { zoomContainerRef, isPanningRef, scaleRef, lastPointerRef } = refs

  if (!zoomContainerRef.current) return

  // only start panning when scaled
  if (scaleRef.current <= 1) return

  isPanningRef.current = true
  lastPointerRef.current = { x: e.clientX, y: e.clientY }

  // capture the pointer for consistent events
  try {
    e.target.setPointerCapture?.(e.pointerId)
  } catch (err) {
    console.log('Error setting pointer capture:', err)
  }
}

/**
 * Handle pointer move for panning
 * @param {object} refs - object containing isPanningRef, scaleRef, lastPointerRef, translateRef
 * @param {function} forceRerender - function to trigger re-render
 * @returns {function} pointer move handler
 */
export const handlePointerMove = (refs, forceRerender) => (e) => {
  const { isPanningRef, scaleRef, lastPointerRef, translateRef } = refs

  if (!isPanningRef.current) return

  const dx = e.clientX - lastPointerRef.current.x
  const dy = e.clientY - lastPointerRef.current.y

  lastPointerRef.current = { x: e.clientX, y: e.clientY }

  // Pointer deltas are in screen pixels; convert to image-space pan deltas
  const scale = scaleRef.current || 1

  translateRef.current = {
    x: translateRef.current.x + dx / scale,
    y: translateRef.current.y + dy / scale
  }

  forceRerender((n) => n + 1)
}

/**
 * Handle pointer up to end panning
 * @param {object} refs - object containing isPanningRef
 * @param {function} adjustBounds - function to adjust translate bounds after pan
 * @returns {function} pointer up handler
 */
export const handlePointerUp = (refs, adjustBounds) => (e) => {
  const { isPanningRef } = refs

  isPanningRef.current = false
  try {
    e.target.releasePointerCapture?.(e.pointerId)
  } catch (err) {
    console.log('Error releasing pointer capture:', err)
  }
  // clamp after panning
  adjustBounds()
}

/**
 * Get transform style string for zoomed/panned image
 * @param {object} refs - object containing scaleRef, translateRef
 * @returns {string} CSS transform string
 */
export const getTransformStyle = (refs) => {
  const { scaleRef, translateRef } = refs

  return `translate(-50%, -50%) translate(${translateRef.current.x}px, ${translateRef.current.y}px) scale(${scaleRef.current})`
}
